<template>
  <div class="flex flex-col h-full">
    <div class="px-4 py-2.5 bg-white flex items-center gap-3" style="border-bottom: 1px solid #E8E9E9;">
      <h2 class="text-lg font-semibold" style="color: #353537;">Summary</h2>
    </div>

    <div style="border-bottom: 1px solid #E8E9E9;">
      <div class="px-4 pt-3 pb-1">
        <div class="flex items-center gap-3">
          <div class="flex flex-shrink-0 items-center rounded border overflow-hidden text-xs" style="border-color: #E8E9E9;">
            <button
              v-for="option in CHART_VIEW_OPTIONS"
              :key="option.value"
              class="px-2.5 py-1 whitespace-nowrap"
              :style="chartView === option.value ? 'background: #21426C; color: #fff;' : 'background: #fff; color: #626264;'"
              @click="chartView = option.value"
            >{{ option.label }}</button>
          </div>
          <div v-if="chartView === 'work'" class="flex flex-shrink-0 items-center rounded border overflow-hidden text-xs" style="border-color: #E8E9E9;">
            <button
              v-for="option in PERIOD_OPTIONS"
              :key="option.value"
              class="px-2.5 py-1 whitespace-nowrap"
              :style="period === option.value ? 'background: #21426C; color: #fff;' : 'background: #fff; color: #626264;'"
              @click="setPeriod(option.value)"
            >{{ option.label }}</button>
          </div>
          <div class="w-64 flex-shrink-0">
            <SimpleMultiSelect
              v-model="workTypeFilter"
              :options="workTypeOptions"
              :sensitive-labels="false"
              placeholder="All work"
              @change="queueFilterFetch"
            />
          </div>
          <div class="w-72 flex-shrink-0">
            <SimpleMultiSelect
              v-model="measureFilter"
              :options="measureOptions"
              :sensitive-labels="false"
              placeholder="All measures"
              :searchable="true"
              @change="queueFilterFetch"
            />
          </div>
          <div class="w-72 flex-shrink-0">
            <SimpleMultiSelect
              v-model="contractFilter"
              :options="contractOptions"
              :sensitive-labels="false"
              placeholder="All contracts"
              :searchable="true"
              @change="queueFilterFetch"
            />
          </div>
        </div>
        <div v-if="chartView === 'work'" class="mt-1 text-xs" style="color: #A5A5A6;">
          {{ period === 'month' ? `last ${trend ? trend.length : 7} months, current month to date` : `last ${trend ? trend.length : 26} complete weeks` }} &middot;
          work: {{ workTypeFilter.length ? workTypeOptions.filter(o => workTypeFilter.includes(o.value)).map(o => o.label).join(', ') : 'care gap MRR, patient call and action outcome, plus medication adherence pharmacy, patient and provider calls' }} &middot;
          signal outcomes: worked gaps shown compliant in the first payer report after the work (counted in the report's {{ period }}), plus adherence refills dated the day of the call (counted in the call's {{ period }})
        </div>
        <div v-else class="mt-1 text-xs" style="color: #A5A5A6;">
          share of the {{ coverage ? coverage.total.patients.toLocaleString() : '' }} unique patients with a {{ coverageYear }} gap on the worklist ({{ coverageProgramLabel }}; Medicare ACO and MSSP contracts excluded) whose gaps have had each kind of work &middot;
          touched: any work on the patient's gaps, including a practice list &middot;
          chart review or call: individual work only &middot;
          dashed: projected to December 31 at the pace of the last {{ coverage && coverage.pace ? Math.round(coverage.pace.days / 7) : 8 }} weeks
        </div>
      </div>
      <div v-if="summary || coverage" class="px-4 pt-2 pb-1 flex items-stretch gap-3">
        <div v-if="summary" class="rounded border px-4 py-2.5 min-w-[150px] flex-shrink-0" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">Work</div>
          <div class="text-2xl font-semibold tabular-nums" style="color: #21426C;">{{ summary.work.toLocaleString() }}</div>
          <div class="text-xs" style="color: #A5A5A6;">{{ windowLabel }}</div>
        </div>
        <div v-if="coverage && coverage.total.patients" class="rounded border px-4 py-2.5 w-[440px] flex-shrink-0" :style="{ borderColor: '#E8E9E9', opacity: coverageLoading ? 0.6 : 1 }">
          <div class="text-xs" style="color: #626264;">Patients touched &middot; {{ coverageYear }}</div>
          <div class="text-2xl font-semibold tabular-nums" style="color: #21426C;">
            {{ formatPct(coverage.total.reached, coverage.total.patients) }}
            <span class="text-sm font-normal" style="color: #626264;">{{ coverage.total.reached.toLocaleString() }} of {{ coverage.total.patients.toLocaleString() }} patients with a gap</span>
          </div>
          <div class="text-xs tabular-nums" style="color: #626264;">
            {{ formatPct(coverage.total.touched, coverage.total.patients) }} by chart review or call ({{ coverage.total.touched.toLocaleString() }})
          </div>
          <div class="flex h-2 mt-1.5 rounded overflow-hidden" style="background: #F3F4F4;">
            <div
              v-for="segment in coverageSegments(coverage.total)"
              :key="segment.key"
              :title="`${segment.label}: ${segment.count.toLocaleString()}`"
              :style="{ width: `${segment.share * 100}%`, background: segment.color }"
            />
          </div>
          <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums" style="color: #626264;">
            <span v-for="segment in coverageSegments(coverage.total)" :key="segment.key" class="flex items-center gap-1 whitespace-nowrap">
              <span class="inline-block w-2 h-2 rounded-sm" :style="{ background: segment.color }" />
              {{ segment.label }} {{ segment.count.toLocaleString() }}
            </span>
          </div>
          <div class="mt-1 flex items-center gap-3 text-xs" style="color: #A5A5A6;">
            <span v-if="coverageYearEnd">projected {{ formatPct(coverageYearEnd.reached, coverage.total.patients) }} by Dec 31</span>
            <button class="underline" style="color: #21426C;" @click="showContracts = !showContracts">
              {{ showContracts ? 'Hide contracts' : 'By contract' }}
            </button>
          </div>
        </div>
        <div v-if="summary" class="rounded border px-4 py-2.5 min-w-[190px] flex-shrink-0" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">With confirmed payer outcome</div>
          <div class="text-2xl font-semibold tabular-nums" style="color: #389A6C;">{{ summary.pct }}</div>
          <div class="text-xs" style="color: #A5A5A6;">{{ summary.withSignal.toLocaleString() }} of {{ summary.work.toLocaleString() }} work items</div>
        </div>
        <div v-if="lift" class="rounded border px-4 py-2.5 flex-1 min-w-0" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">Lift from the team's work</div>
          <div v-if="lift.total.measurable" class="text-2xl font-semibold tabular-nums" style="color: #21426C;">
            {{ lift.total.extra >= 0 ? '+' : '' }}{{ Math.round(lift.total.extra).toLocaleString() }}
            <span class="text-sm font-normal" style="color: #626264;">extra outcomes (95% {{ Math.round(lift.total.low).toLocaleString() }} to {{ Math.round(lift.total.high).toLocaleString() }})</span>
          </div>
          <div v-else class="text-2xl font-semibold" style="color: #A5A5A6;">No measurable lift</div>
          <div v-for="row in lift.rows" :key="row.program" class="text-xs tabular-nums" :style="{ color: row.measurable ? '#23734F' : '#A5A5A6' }">
            {{ row.detail }}
            <span v-if="row.partialWorkTypes" style="color: #A5A5A6;">&middot; practice-level: covers all individual care gap work, not just the selected types</span>
          </div>
        </div>
      </div>
      <div v-if="showContracts && coverage && coverage.total.patients" class="px-4 pt-2 pb-1">
        <div class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="w-full text-xs tabular-nums">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th
                v-for="col in contractColumns"
                :key="col.field"
                class="px-3 py-2 font-medium text-gray-500 whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                :class="col.align === 'right' ? 'text-right' : 'text-left'"
                @click="toggleContractSort(col.field)"
              >
                <span class="inline-flex items-center gap-1" :class="col.align === 'right' ? 'justify-end' : ''">
                  <span v-if="col.color" class="inline-block w-2 h-2 rounded-sm" :style="{ background: col.color }" />
                  {{ col.header }}<span class="text-[9px]">{{ sortIndicator(contractSort, col.field) }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in contractRows"
              :key="row.contract_name"
              class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              :class="row.isTotal ? 'font-semibold bg-gray-50' : ''"
            >
              <td v-for="col in contractColumns" :key="col.field" class="px-3 py-2 whitespace-nowrap" :class="col.align === 'right' ? 'text-right' : ''">
                <template v-if="col.field === 'contract_name'">{{ row.contract_name }}</template>
                <template v-else-if="col.field === 'touched' || col.field === 'reached'">
                  {{ row[col.field].toLocaleString() }} <span class="text-gray-400">({{ formatPct(row[col.field], row.patients) }})</span>
                </template>
                <template v-else>{{ row[col.field].toLocaleString() }}</template>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <div class="px-2 pb-2 relative">
        <template v-if="chartView === 'coverage'">
          <LoadingIndicator v-if="coverageLoading && !coverage" />
          <div v-else-if="coverageError" class="px-2 py-6 text-sm" style="color: #B91C1C;">{{ coverageError }}</div>
          <div v-else-if="!coverage || !coverage.total.patients" class="px-2 py-6 text-sm" style="color: #626264;">
            No patients with a gap this year for these filters.
          </div>
          <Chart_V5 v-else :chart_data="coverageChart" :chart_config="coverageConfig" override-height="h-[320px]" />
          <div
            v-if="coverageLoading && coverage"
            class="absolute inset-0 flex items-center justify-center"
            style="background: rgba(255, 255, 255, 0.6);"
          >
            <LoadingIndicator />
          </div>
        </template>
        <template v-else>
        <LoadingIndicator v-if="loading && !trend" />
        <div v-else-if="error" class="px-2 py-6 text-sm" style="color: #B91C1C;">{{ error }}</div>
        <div v-else-if="!trend || !trend.some(w => w.work_count)" class="px-2 py-6 text-sm" style="color: #626264;">
          No work logged in this window.
        </div>
        <Chart_V5 v-else :chart_data="trendChart" :chart_config="trendConfig" override-height="h-[320px]" />
        <div
          v-if="loading && trend"
          class="absolute inset-0 flex items-center justify-center"
          style="background: rgba(255, 255, 255, 0.6);"
        >
          <LoadingIndicator />
        </div>
        </template>
      </div>
    </div>

    <div>
      <div class="px-4 pt-3 pb-2 flex items-center gap-3">
        <span class="text-sm font-semibold" style="color: #353537;">Work &rarr; signal outcome</span>
        <span class="text-xs" style="color: #A5A5A6;">
          every piece of work in the last 26 weeks matching the filters above, with the signal outcome tied to it
        </span>
        <div class="flex-1"></div>
        <span class="text-xs tabular-nums" style="color: #626264;">{{ logTotal.toLocaleString() }} rows</span>
      </div>
      <LoadingIndicator v-if="logLoading" />
      <div v-else-if="logError" class="px-4 py-6 text-sm" style="color: #B91C1C;">{{ logError }}</div>
      <div v-else-if="!logRows.length" class="px-4 py-6 text-sm" style="color: #626264;">No work logged in this window.</div>
      <div v-else class="px-4 pb-4">
        <div class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="w-full text-xs">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th
                v-for="col in LOG_COLUMNS"
                :key="col.field"
                class="px-3 py-2 font-medium text-gray-500 text-left whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                @click="toggleLogSort(col.field)"
              >
                <span class="inline-flex items-center gap-1">
                  {{ col.header }}<span class="text-[9px]">{{ sortIndicator(logSort, col.field) }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in logRows" :key="row.work_id" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td class="px-3 py-2 whitespace-nowrap tabular-nums">{{ formatDateTime(row.work_created_at) }}</td>
              <td class="px-3 py-2 whitespace-nowrap sensitive">{{ row.worker_entity_name || '—' }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ row.work_type }}</td>
              <td class="px-3 py-2">{{ formatOutcome(row.outcome) }}</td>
              <td class="px-3 py-2 whitespace-nowrap sensitive">{{ [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', ') || '—' }}</td>
              <td class="px-3 py-2 whitespace-nowrap tabular-nums sensitive">{{ row.patient_dob || '—' }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ row.measure_code || '—' }}</td>
              <td class="px-3 py-2">{{ row.contract_name || '—' }}</td>
              <td class="px-3 py-2 sensitive">{{ row.practice_name || '—' }}</td>
              <td class="px-3 py-2" :style="{ color: row.has_signal ? '#23734F' : '#626264', fontWeight: row.has_signal ? 600 : 400 }">{{ row.signal_outcome }}</td>
              <td class="px-3 py-2 whitespace-nowrap tabular-nums">{{ row.signal_date || '' }}</td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="flex items-center justify-end gap-3 pt-3 text-xs" style="color: #626264;">
          <span class="tabular-nums">{{ (logStart + 1).toLocaleString() }}–{{ Math.min(logStart + LOG_PAGE_SIZE, logTotal).toLocaleString() }} of {{ logTotal.toLocaleString() }}</span>
          <button class="px-2 py-1 rounded border" style="border-color: #E8E9E9;" :disabled="logStart === 0" @click="pageLog(-1)">Previous</button>
          <button class="px-2 py-1 rounded border" style="border-color: #E8E9E9;" :disabled="logStart + LOG_PAGE_SIZE >= logTotal" @click="pageLog(1)">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import abortable from '@/mixins/abortable';

const LOG_PAGE_SIZE = 100;
const FILTER_SETTLE_MS = 600;
const LIFT_MIN_UNITS = 30;
const LIFT_MIN_PRACTICES = 5;
const LIFT_BOOTSTRAP_DRAWS = 400;
const LIFT_BOOTSTRAP_SEED = 20260927;
const CARE_GAP_WORK_TYPES = ['medical_record_review_care_gaps', 'care_gap_patient_call', 'care_gap_action_outcome'];

function formatSigned(value, digits) {
  const rounded = Number(value.toFixed(digits));
  return `${rounded >= 0 ? '+' : ''}${digits ? rounded.toFixed(digits) : Math.round(rounded).toLocaleString()}`;
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
];

const CHART_VIEW_OPTIONS = [
  { value: 'work', label: 'Work and outcomes' },
  { value: 'coverage', label: 'Patient coverage' },
];

const COVERAGE_SEGMENTS = [
  { key: 'chart_and_call', label: 'Chart review and call', color: '#21426C' },
  { key: 'chart_only', label: 'Chart review only', color: '#4A78B0' },
  { key: 'call_only', label: 'Call only', color: '#8FB3DE' },
  { key: 'other_only', label: 'Other work only', color: '#C4D6EC' },
  { key: 'list_only', label: 'Practice list only', color: '#E3B462' },
  { key: 'untouched', label: 'Not touched', color: '#E8E9E9' },
];

const LOG_COLUMNS = [
  { field: 'work_created_at', header: 'When' },
  { field: 'worker_entity_name', header: 'Worker' },
  { field: 'work_type', header: 'Work' },
  { field: 'outcome', header: 'Outcome logged' },
  { field: 'patient', header: 'Patient' },
  { field: 'patient_dob', header: 'DOB' },
  { field: 'measure_code', header: 'Measure' },
  { field: 'contract_name', header: 'Contract' },
  { field: 'practice_name', header: 'Practice' },
  { field: 'signal_outcome', header: 'Signal outcome' },
  { field: 'signal_date', header: 'Signal date' },
];

const COVERAGE_TOUCHED_COLOR = '#21426C';
const COVERAGE_REACHED_COLOR = '#E3B462';
const COVERAGE_PROJECTION_DASH = '6 4';

export default {
  name: 'Quality_Summary',
  components: { Chart_V5, LoadingIndicator, SimpleMultiSelect },
  mixins: [abortable],

  data() {
    return {
      trend: null,
      period: 'week',
      PERIOD_OPTIONS,
      CHART_VIEW_OPTIONS,
      COVERAGE_SEGMENTS,
      chartView: 'work',
      workTypeFilter: [],
      workTypeOptions: [],
      measureFilter: [],
      measureOptions: [],
      contractFilter: [],
      contractOptions: [],
      coverage: null,
      coverageLoading: false,
      coverageError: null,
      showContracts: false,
      LOG_COLUMNS,
      logSort: { field: 'work_created_at', dir: 'desc' },
      contractSort: { field: 'patients', dir: 'desc' },
      liftUnits: [],
      liftCells: [],
      loading: false,
      error: null,
      logRows: [],
      logTotal: 0,
      logStart: 0,
      logLoading: false,
      logError: null,
      LOG_PAGE_SIZE,
    };
  },

  computed: {
    lift() {
      if (!this.liftUnits.length && !this.liftCells.length) return null;
      const types = new Set(this.workTypeFilter);
      const measures = new Set(this.measureFilter);
      const measureOk = code => !measures.size || measures.has(code);
      const contracts = new Set(this.contractFilter);
      const contractOk = name => !contracts.size || contracts.has(name);
      const rows = [];

      const adherenceUnits = this.liftUnits.filter(u =>
        (!types.size || u.work_types.some(t => types.has(t))) && measureOk(u.measure_code) && contractOk(u.contract_name)
      );
      if (adherenceUnits.length) rows.push(this.adherenceLift(adherenceUnits));

      const gapTypesSelected = CARE_GAP_WORK_TYPES.filter(t => types.has(t));
      if (!types.size || gapTypesSelected.length) {
        const cells = this.liftCells.filter(c => measureOk(c.measure_code) && contractOk(c.contract_name));
        const gap = this.practiceLift(cells);
        if (gap) {
          gap.partialWorkTypes = types.size > 0 && gapTypesSelected.length < CARE_GAP_WORK_TYPES.length;
          rows.push(gap);
        }
      }
      if (!rows.length) return null;
      const extra = rows.reduce((a, r) => a + r.extra, 0);
      const low = extra - Math.sqrt(rows.reduce((a, r) => a + (r.extra - r.low) ** 2, 0));
      const high = extra + Math.sqrt(rows.reduce((a, r) => a + (r.high - r.extra) ** 2, 0));
      return { total: { extra, low, high, measurable: rows.some(r => r.measurable) }, rows };
    },
    summary() {
      if (!this.trend || !this.trend.length) return null;
      const work = this.trend.reduce((a, w) => a + w.work_count, 0);
      const withSignal = this.trend.reduce((a, w) => a + w.work_with_signal, 0);
      return { work, withSignal, pct: work ? `${((withSignal / work) * 100).toFixed(1)}%` : '—' };
    },
    contractColumns() {
      return [
        { field: 'contract_name', header: 'Contract' },
        { field: 'patients', header: 'Patients with a gap', align: 'right' },
        { field: 'open_now', header: 'Open gap now', align: 'right' },
        { field: 'reached', header: 'Touched', align: 'right' },
        { field: 'touched', header: 'Chart review or call', align: 'right' },
        ...COVERAGE_SEGMENTS
          .filter(s => s.key !== 'other_only' || this.coverage.total.other_only)
          .map(s => ({ field: s.key, header: s.label, align: 'right', color: s.color })),
      ];
    },
    contractRows() {
      const { field, dir } = this.contractSort;
      const d = dir === 'asc' ? 1 : -1;
      const sorted = [...this.coverage.contracts].sort((a, b) => (
        typeof a[field] === 'string' ? d * a[field].localeCompare(b[field]) : d * (a[field] - b[field])
      ));
      return [{ ...this.coverage.total, contract_name: 'All contracts (each patient once)', isTotal: true }, ...sorted];
    },
    coverageYear() {
      return this.coverage && this.coverage.months.length ? this.coverage.months[0].month.slice(0, 4) : new Date().getFullYear();
    },
    coverageProgramLabel() {
      const programs = this.coverage ? this.coverage.programs : [];
      if (programs.length === 1) return programs[0] === 'medication_adherence' ? 'medication adherence' : 'care gaps';
      return 'care gaps and medication adherence';
    },
    coverageYearEnd() {
      if (!this.coverage || !this.coverage.months.length) return null;
      const last = this.coverage.months[this.coverage.months.length - 1];
      return last.projected ? last : null;
    },
    coverageChart() {
      const months = this.coverage.months;
      const total = this.coverage.total.patients;
      const lastActual = months.reduce((idx, m, i) => (m.projected ? idx : i), -1);
      const hasProjection = months.some(m => m.projected);
      const actual = key => months.map((m, i) => (i <= lastActual ? m[key] / total : null));
      const projected = key => months.map((m, i) => (hasProjection && i >= lastActual ? m[key] / total : null));
      const tooltip = (label, key) => ({
        formatter: point => {
          const m = months[point.index];
          return `<strong>${label}${m.projected ? ' (projected)' : ''}</strong><br/>${point.label}: ${m[key].toLocaleString()} of ${total.toLocaleString()} patients (${this.formatPct(m[key], total)})`;
        },
      });
      const line = (label, key, color, dashed, values) => ({
        values,
        config: {
          label,
          backgroundColor: color,
          type: 'line',
          yAxis: 'left',
          offset: 0,
          strokeDasharray: dashed ? COVERAGE_PROJECTION_DASH : null,
          tooltip: tooltip(label.replace(', projected', ''), key),
        },
      });
      return {
        labels: months.map(m => {
          const [year, month] = m.month.split('-').map(Number);
          return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short' });
        }),
        series: [
          line('Touched (any work, incl. practice list)', 'reached', COVERAGE_REACHED_COLOR, false, actual('reached')),
          line('Touched, projected', 'reached', COVERAGE_REACHED_COLOR, true, projected('reached')),
          line('Chart review or call', 'touched', COVERAGE_TOUCHED_COLOR, false, actual('touched')),
          line('Chart review or call, projected', 'touched', COVERAGE_TOUCHED_COLOR, true, projected('touched')),
        ],
      };
    },
    coverageConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 60, right: 40, bottom: 50, left: 60 },
        xAxis: { labelRotation: 0, fontSize: 11 },
        yAxes: [{ position: 'left', min: 0, max: null, fontSize: 11 }],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: true, currency: false, sig_figs: 0 },
        hideDataLabels: true,
      };
    },
    windowLabel() {
      const n = this.trend ? this.trend.length : 0;
      return this.period === 'month' ? `last ${n} months, current month to date` : `last ${n} complete weeks`;
    },
    trendChart() {
      return {
        labels: this.trend.map(w => this.formatPeriod(w)),
        series: [
          {
            values: this.trend.map(w => w.work_count),
            config: {
              label: 'Work (left axis)',
              backgroundColor: '#21426C',
              type: 'line',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: this.trend.map(w => w.signal_outcomes),
            config: {
              label: 'Signal outcomes (right axis)',
              backgroundColor: '#389A6C',
              type: 'line',
              yAxis: 'right',
              offset: 0,
            },
          },
        ],
      };
    },
    trendConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 60, right: 70, bottom: 70, left: 60 },
        xAxis: { labelRotation: -45, fontSize: 11 },
        yAxes: [
          { position: 'left', min: 0, max: null, fontSize: 11 },
          { position: 'right', min: 0, max: null, fontSize: 11 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 11 },
      };
    },
  },

  beforeUnmount() {
    clearTimeout(this._filterTimer);
  },

  mounted() {
    this.contractFilter = this.loadSavedContracts();
    this.fetchTrend();
    this.fetchCoverage();
    this.fetchLog();
    this.fetchLiftUnits();
  },

  methods: {
    adherenceLift(units) {
      const matched = units.filter(u => u.expected !== null);
      const actual = matched.reduce((a, u) => a + u.outcome, 0);
      const expected = matched.reduce((a, u) => a + u.expected, 0);
      const variance = matched.reduce((a, u) => a + u.expected * (1 - u.expected), 0);
      const n = matched.length;
      const extra = actual - expected;
      const ci = 1.96 * Math.sqrt(variance);
      const points = n ? (extra / n) * 100 : 0;
      return {
        program: 'med_adherence',
        extra,
        low: extra - ci,
        high: extra + ci,
        measurable: n >= LIFT_MIN_UNITS && Math.abs(extra) > ci,
        detail: n
          ? `Adherence calls, refilled same day: ${((actual / n) * 100).toFixed(1)}% vs ${((expected / n) * 100).toFixed(1)}% expected (${formatSigned(points, 1)} pts) · n ${n.toLocaleString()}${n < units.length ? ` of ${units.length.toLocaleString()}` : ''}`
          : 'Adherence calls: no episodes with enough comparable patients',
      };
    },
    practiceLift(cells) {
      const byPractice = new Map();
      for (const c of cells) {
        const p = byPractice.get(c.practice_key) || { pre: { n: 0, x: 0 }, post: { n: 0, x: 0 } };
        const side = c.event_time < 0 ? p.pre : p.post;
        side.n += c.open_gaps;
        side.x += c.closed - c.expected_closed;
        byPractice.set(c.practice_key, p);
      }
      const practices = [...byPractice.values()].filter(p => p.pre.n && p.post.n);
      if (!practices.length) return null;
      const estimate = sample => {
        const t = sample.reduce((a, p) => ({ pn: a.pn + p.pre.n, px: a.px + p.pre.x, qn: a.qn + p.post.n, qx: a.qx + p.post.x }), { pn: 0, px: 0, qn: 0, qx: 0 });
        const rate = t.qx / t.qn - t.px / t.pn;
        return { rate, extra: rate * t.qn, postGaps: t.qn };
      };
      const point = estimate(practices);
      const random = seededRandom(LIFT_BOOTSTRAP_SEED);
      const draws = Array.from({ length: LIFT_BOOTSTRAP_DRAWS }, () =>
        estimate(Array.from({ length: practices.length }, () => practices[Math.floor(random() * practices.length)])).extra
      ).sort((x, y) => x - y);
      const low = draws[Math.floor(0.025 * draws.length)];
      const high = draws[Math.ceil(0.975 * draws.length) - 1];
      return {
        program: 'care_gap',
        extra: point.extra,
        low,
        high,
        measurable: practices.length >= LIFT_MIN_PRACTICES && (low > 0 || high < 0),
        detail: `Care gaps, practices before vs after the team started: ${formatSigned(point.rate * 100, 2)} pts on ${Math.round(point.postGaps).toLocaleString()} open gaps · ${formatSigned(point.extra, 0)} closures (95% ${formatSigned(low, 0)} to ${formatSigned(high, 0)}) · ${practices.length} practices`,
      };
    },
    sortIndicator(sort, field) {
      if (sort.field !== field) return '';
      return sort.dir === 'asc' ? '▲' : '▼';
    },
    nextSort(sort, field) {
      if (sort.field === field) return { field, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
      return { field, dir: field === 'contract_name' ? 'asc' : 'desc' };
    },
    toggleContractSort(field) {
      this.contractSort = this.nextSort(this.contractSort, field);
    },
    toggleLogSort(field) {
      this.logSort = this.nextSort(this.logSort, field);
      this.logStart = 0;
      this.fetchLog();
    },
    formatPct(count, total) {
      if (!total) return '—';
      const pct = (count / total) * 100;
      return `${pct > 0 && pct < 1 ? pct.toFixed(1) : Math.round(pct)}%`;
    },
    coverageSegments(row) {
      return COVERAGE_SEGMENTS
        .filter(segment => row[segment.key] > 0)
        .map(segment => ({ ...segment, count: row[segment.key], share: row.patients ? row[segment.key] / row.patients : 0 }));
    },
    filterParams() {
      return {
        workTypes: this.workTypeFilter.join(','),
        measures: this.measureFilter.join('|'),
        contracts: this.contractFilter.join('|'),
      };
    },
    formatOutcome(outcome) {
      if (!outcome) return '—';
      if (!outcome.includes('_')) return outcome;
      const s = outcome.replace(/_+$/, '').replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    setPeriod(value) {
      if (value === this.period) return;
      this.period = value;
      this.fetchTrend();
    },
    formatPeriod(row) {
      if (this.period === 'week') return row.period_start;
      const [year, month] = row.period_start.split('-').map(Number);
      const label = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
      return row.is_partial ? `${label} (to date)` : label;
    },
    formatDateTime(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    pageLog(direction) {
      this.logStart = Math.max(0, this.logStart + direction * LOG_PAGE_SIZE);
      this.fetchLog();
    },
    async fetchLog() {
      const signal = this._renewAbortSignal('log');
      this.logLoading = true;
      this.logError = null;
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', {
          params: {
            lookup: 'work_signal_log',
            startRow: this.logStart,
            endRow: this.logStart + LOG_PAGE_SIZE,
            sortField: this.logSort.field,
            sortDir: this.logSort.dir,
            ...this.filterParams(),
          },
          signal,
        });
        this.logRows = data.rows;
        this.logTotal = data.total;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.logError = e?.response?.data?.error || 'Could not load work log.';
        this.logRows = [];
        this.logTotal = 0;
      } finally {
        if (!signal.aborted) this.logLoading = false;
      }
    },
    async fetchCoverage() {
      const signal = this._renewAbortSignal('coverage');
      this.coverageLoading = true;
      this.coverageError = null;
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', {
          params: { lookup: 'patient_coverage', ...this.filterParams() },
          signal,
        });
        this.coverage = data;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.coverageError = e?.response?.data?.error || 'Could not load patient coverage.';
        this.coverage = null;
      } finally {
        if (!signal.aborted) this.coverageLoading = false;
      }
    },
    async fetchLiftUnits() {
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', { params: { lookup: 'lift_units' } });
        this.liftUnits = data.units;
        this.liftCells = data.practice_cells;
      } catch (e) {
        this.liftUnits = [];
        this.liftCells = [];
      }
    },
    queueFilterFetch() {
      clearTimeout(this._filterTimer);
      this._filterTimer = setTimeout(() => this.applyFilters(), FILTER_SETTLE_MS);
    },
    contractStorageKey() {
      return `quality_summary_contracts:${this.$store.getters['tenant/current_tenant_slug'] || 'default'}`;
    },
    loadSavedContracts() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.contractStorageKey()) || '[]');
        return Array.isArray(saved) ? saved : [];
      } catch (e) {
        return [];
      }
    },
    applyFilters() {
      clearTimeout(this._filterTimer);
      localStorage.setItem(this.contractStorageKey(), JSON.stringify(this.contractFilter));
      this.logStart = 0;
      this.fetchTrend();
      this.fetchCoverage();
      this.fetchLog();
    },
    async fetchTrend() {
      const signal = this._renewAbortSignal('trend');
      this.loading = true;
      this.error = null;
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', {
          params: { lookup: 'weekly_trend', period: this.period, ...this.filterParams() },
          signal,
        });
        this.trend = data.trend;
        if (!this.workTypeOptions.length) {
          this.workTypeOptions = data.work_type_options.map(o => ({ value: o.value, label: `${o.label} (${o.program})` }));
        }
        if (!this.measureOptions.length) this.measureOptions = data.measure_options;
        if (!this.contractOptions.length) {
          this.contractOptions = data.contract_options;
          const known = new Set(this.contractOptions.map(o => o.value));
          const kept = this.contractFilter.filter(c => known.has(c));
          if (kept.length !== this.contractFilter.length) {
            this.contractFilter = kept;
            this.applyFilters();
          }
        }
      } catch (e) {
        if (this._isAborted(e)) return;
        this.error = e?.response?.data?.error || 'Could not load work trend.';
        this.trend = null;
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },

  },
};
</script>
